import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn.js';

/**
 * Les dialogues ouverts, du plus ancien au plus récent. Deux usages : ne rendre son défilement
 * au <body> qu'à la fermeture du dernier, et dire aux notifications où se rendre — un <dialog>
 * modal rend inerte tout ce qui n'est pas en lui, une notification restée dans la page serait
 * sous son voile et incliquable (`components/Toast.tsx`).
 */
let pile: HTMLDialogElement[] = [];
const abonnes = new Set<() => void>();

function changerPile(suivante: HTMLDialogElement[]): void {
  pile = suivante;
  document.body.style.overflow = pile.length > 0 ? 'hidden' : '';
  for (const prevenir of abonnes) prevenir();
}

/**
 * L'élément focalisé à l'ouverture n'est plus dans le DOM (l'action d'un toast, recréée par le
 * portail quand le dialogue s'ouvre) : `close()` laisserait le focus sur le <body>. Il va au
 * dialogue qui reste dessous, sinon au <main>, sans faire défiler la page.
 */
function rendreLeFocusAuRepli(ferme: HTMLDialogElement): void {
  // `close()` n'a eu nulle part où rendre le focus : il est resté sur un champ du dialogue fermé
  // (qui va quitter le DOM, et lui avec), ou il est déjà retombé sur le <body>.
  const actif = document.activeElement;
  if (actif && actif !== document.body && !ferme.contains(actif)) return;
  const repli = pile[pile.length - 1] ?? document.querySelector<HTMLElement>('main');
  if (!repli) return;
  if (!repli.hasAttribute('tabindex')) repli.tabIndex = -1;
  repli.focus({ preventScroll: true });
}

/** Le dialogue du dessus, ou `null` quand aucun n'est ouvert. */
export function useDialogueDuDessus(): HTMLDialogElement | null {
  return useSyncExternalStore(
    (prevenir) => {
      abonnes.add(prevenir);
      return () => abonnes.delete(prevenir);
    },
    () => pile[pile.length - 1] ?? null
  );
}

export interface DialogueProps {
  open: boolean;
  onClose: () => void;
  /** Rendu dans `.dialogue-entete`, en <h2>, à côté du bouton de fermeture. */
  title?: ReactNode;
  /** Remplace le titre dans l'en-tête (une saisie de recherche) ; le bouton de fermeture reste. */
  header?: ReactNode;
  /** Les boutons, dans `.dialogue-pied` ; une action à gauche porte `className="mr-auto"`. */
  footer?: ReactNode;
  /** `sm` : une confirmation ; `lg` : un formulaire large, une recherche. */
  size?: 'sm' | 'md' | 'lg';
  /** `top` : ancré sous le haut de l'écran — quand le contenu change de hauteur à la frappe. */
  anchor?: 'center' | 'top';
  /** `feuille` : ancré en bas (téléphone) ; `tiroir` : le panneau de gauche. */
  forme?: 'feuille' | 'tiroir';
  /** Un menu ou des suggestions peuvent dépasser du dialogue (il ne défile alors plus). */
  debordant?: boolean;
  /** Sans `.dialogue-corps` : l'appelant pose sa propre structure (le tiroir). */
  nu?: boolean;
  closeLabel?: string;
  hideClose?: boolean;
  /** Par défaut : le premier élément focalisable du dialogue (comportement natif). */
  initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  'aria-label'?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Le `<dialog>` natif du kit (`.dialogue`), piloté par React.
 *
 * `showModal()` apporte le piège à focus, `Escape`, le voile, l'inertie de la page et le retour
 * du focus à la fermeture. Ce qui reste à faire ici, c'est de garder `open` comme seule source
 * de vérité :
 *   - `cancel` (Escape) est intercepté et remonté par `onClose` — l'appelant peut refuser
 *     (une tâche modifiée demande confirmation) ;
 *   - `close` : Chrome n'honore un `cancel` empêché qu'une fois par activation, un second
 *     Escape ferme l'élément quoi qu'on fasse. On remonte `onClose`, puis on rouvre si l'appelant
 *     a refusé ;
 *   - un clic sur le voile ferme. Le test est géométrique ET sur la cible : le padding d'une
 *     feuille appartient au dialogue (un tap sous le dernier bouton ne doit pas fermer), et le
 *     voile d'un dialogue imbriqué (une confirmation) n'est pas celui de son parent.
 *
 * Les raccourcis globaux (`useKeyboard`) se taisent tant qu'un dialogue est ouvert : un `Escape`
 * qu'ils auraient consommé (`preventDefault`) n'arriverait jamais au dialogue — le navigateur
 * n'émet pas `cancel` pour une touche annulée. C'est aussi ce qui laisse un composant garder
 * son Escape : le menu des projets d'une saisie se ferme sans fermer le dialogue.
 *
 * Rien n'est rendu tant que `open` est faux.
 */
export function Dialogue(props: DialogueProps): React.ReactElement | null {
  if (!props.open) return null;
  return <DialogueOuvert {...props} />;
}

function DialogueOuvert({
  onClose,
  title,
  header,
  footer,
  size = 'md',
  anchor = 'center',
  forme,
  debordant = false,
  nu = false,
  closeLabel = 'Close',
  hideClose = false,
  initialFocusRef,
  'aria-label': ariaLabel,
  className,
  children,
}: DialogueProps): React.ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [, reverifier] = useState(0);
  const clavier = useDecalageClavier(forme === 'feuille');

  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    const origine = document.activeElement;
    d.showModal();
    initialFocusRef?.current?.focus();
    changerPile([...pile, d]);
    return () => {
      // Fermé avant de quitter le DOM : c'est `close()` qui rend le focus à l'élément d'origine.
      if (d.open) d.close();
      changerPile(pile.filter((x) => x !== d));
      if (origine && !origine.isConnected) rendreLeFocusAuRepli(d);
    };
    // Ouvert une fois, au montage : `open` faux démonte le composant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // L'appelant a refusé la fermeture (voir `close` plus haut) : l'élément se rouvre. Rouvert, il
  // repasse AU-DESSUS de tout dans la couche supérieure — y compris de la confirmation qu'il
  // contient et qui vient de s'ouvrir : elle est donc rouverte à son tour, après lui.
  useEffect(() => {
    const d = ref.current;
    if (!d || d.open) return;
    d.showModal();
    for (const imbrique of d.querySelectorAll<HTMLDialogElement>('dialog[open]')) {
      imbrique.close();
      imbrique.showModal();
    }
  });

  // `cancel` et `close` ne remontent pas dans le DOM, mais React, lui, les fait remonter dans
  // SON arbre : sans le test sur la cible, l'Échap d'une confirmation imbriquée fermait aussi
  // — ou redemandait de fermer — le dialogue qui la contient.
  const onCancel = useCallback(
    (e: React.SyntheticEvent<HTMLDialogElement>) => {
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      onClose();
    },
    [onClose]
  );

  const onCloseNative = useCallback(
    (e: React.SyntheticEvent<HTMLDialogElement>) => {
      if (e.target !== e.currentTarget) return;
      // `close` arrive en différé : s'il est déjà rouvert (StrictMode monte deux fois), il est périmé.
      if (e.currentTarget.open) return;
      onClose();
      reverifier((n) => n + 1);
    },
    [onClose]
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      // Un dialogue est souvent rendu DANS ce qui l'a ouvert (une ligne cliquable) : le clic
      // ne doit pas remonter l'arbre React jusqu'à elle.
      e.stopPropagation();
      if (e.target !== e.currentTarget) return;
      const r = e.currentTarget.getBoundingClientRect();
      const dehors =
        e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      if (dehors) onClose();
    },
    [onClose]
  );

  const style: CSSProperties | undefined =
    clavier > 0 ? { paddingBottom: `${clavier}px` } : undefined;

  const entete = header !== undefined || title !== undefined;

  return (
    <dialog
      ref={ref}
      className={cn(
        'dialogue',
        size === 'sm' && 'dialogue-etroit',
        size === 'lg' && 'dialogue-large',
        anchor === 'top' && !forme && 'dialogue-haut',
        forme === 'feuille' && 'dialogue-feuille',
        forme === 'tiroir' && 'dialogue-panneau dialogue-tiroir',
        debordant && 'dialogue-debordant',
        className
      )}
      style={style}
      aria-labelledby={title !== undefined && header === undefined ? titleId : undefined}
      aria-label={ariaLabel}
      onCancel={onCancel}
      onClose={onCloseNative}
      onClick={onClick}
    >
      {entete && (
        <header className="dialogue-entete">
          {header ?? <h2 id={titleId}>{title}</h2>}
          {!hideClose && (
            <button
              type="button"
              className="bouton bouton-discret bouton-icone"
              aria-label={closeLabel}
              onClick={onClose}
            >
              <X size={20} aria-hidden />
            </button>
          )}
        </header>
      )}
      {nu ? children : <div className="dialogue-corps">{children}</div>}
      {footer !== undefined && <footer className="dialogue-pied">{footer}</footer>}
    </dialog>
  );
}

/**
 * Le clavier d'iOS recouvre ce qui est ancré en bas de l'écran sans réduire la fenêtre : une
 * feuille s'y retrouve à moitié cachée. `visualViewport` dit de combien — la feuille se rembourre
 * d'autant, et son contenu remonte au-dessus du clavier.
 */
function useDecalageClavier(actif: boolean): number {
  const [decalage, setDecalage] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!actif || !vv) return;
    const mesurer = (): void =>
      setDecalage(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    mesurer();
    vv.addEventListener('resize', mesurer);
    vv.addEventListener('scroll', mesurer);
    return () => {
      vv.removeEventListener('resize', mesurer);
      vv.removeEventListener('scroll', mesurer);
    };
  }, [actif]);
  return decalage;
}
